import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, switchMap, take } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Attaches the Cognito access token to calls at our own APIs.
 *
 * Both backends must be covered: EngineService swaps /builder for /pw-builder when the Playwright
 * engine is selected, so matching only environment.apiHost would leave every Playwright call
 * unauthenticated and failing with a 401 that looks like a CORS error in the browser.
 *
 * The token is attached only to our own origins. Requests to anywhere else — a target application
 * under test, a presigned S3 screenshot URL — must never carry it.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  /** Both API base URLs, derived from the one configured host. */
  private readonly apiBases: string[] = (() => {
    const builder = environment.apiHost.replace(/\/+$/, '');
    const pw = builder.replace(/\/builder$/, '/pw-builder');
    return Array.from(new Set([builder, pw]));
  })();

  constructor(private readonly oidc: OidcSecurityService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isOwnApi(req.url)) {
      return next.handle(req);
    }
    return this.oidc.getAccessToken().pipe(
      take(1),
      switchMap(token => next.handle(
        token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req
      ))
    );
  }

  private isOwnApi(url: string): boolean {
    return this.apiBases.some(base => url.startsWith(base));
  }
}
